import {Component, EventEmitter, Input, OnInit, Output, TemplateRef} from '@angular/core'
import {NgbCalendar, NgbDate, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {SchedulesService} from "../../../pages/schedules/schedules.service";
import {
  Offset,
  ScheduleDetails,
  ScheduleEvent,
  ScheduleVersionDetail,
  ScheduleVersionRequest
} from 'src/app/pages/schedules/schedule';
import {ProgramsService} from "../../../pages/programs/programs.service";
import {ConfirmationModalComponent} from "../../confirmation-modal/confirmation-modal.component";
import {SchedulesErrorModalComponent} from "../../../pages/schedules/schedules-error-modal.component";
import {BehaviorSubject} from "rxjs";
import {distinctUntilChanged} from "rxjs/operators";
import {OFFSET_OPTIONS, parseDate, getValidQualifiers, getLangFilters, sortByOrders} from "../../../utils/contants";
import {environment} from "../../../../environments/environment";
import { AppConstants } from 'src/app/app.constants';
import * as dayjs from 'dayjs';
import {ReportProblemModalComponent} from '../../../pages/schedules/report-problem-modal/report-problem-modal.component';

@Component({
  selector: 'app-schedule-panel',
  templateUrl: './schedule-panel.component.html',
  styleUrls: ['./schedule-panel.component.scss'],
})
export class SchedulesPanelComponent implements OnInit {
  // Accessors
  @Input() shouldHideProgramPanel = true;
  @Input() readOnlyMode = false;
  @Input() isReportProblemAvailable = false;

  @Input() set selectedScheduleVersions(selected: ScheduleVersionDetail[]) {
    this.selectedScheduleVersionsSubject.next(selected);
    this.scheduleLoadingSubject.next(true);
    this.originalEventsSnapshot.clear();
  };

  @Input() set selectedChannel(selectedChannel: SourceChannel) {
    this.channelSubject.next(selectedChannel);
  }

  @Output() onScheduleVersionRequestChanged: EventEmitter<ScheduleVersionRequest> = new EventEmitter();
  @Output() onShowOrHideProgramPanel: EventEmitter<boolean> = new EventEmitter();
  @Output() onInvalidProgram: EventEmitter<string> = new EventEmitter();

  // public fields
  ratings: any[];
  qualifiers: any[];
  genres: any[];
  channelSubject: BehaviorSubject<SourceChannel> = new BehaviorSubject(null);
  requestSubject: BehaviorSubject<ScheduleVersionRequest> = new BehaviorSubject({});
  schedulesSubject: BehaviorSubject<ScheduleDetails[]> = new BehaviorSubject([]); // will be read from api
  scheduleLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  selectedScheduleVersionsSubject: BehaviorSubject<ScheduleVersionDetail[]> = new BehaviorSubject([]);
  private TODAY: NgbDate = this.calendar.getToday();
  private DEFAULT_TIMEZONE = environment.timezone
    ? OFFSET_OPTIONS.find(o => o.value === environment.timezone)
    : OFFSET_OPTIONS[0];
  private GMT_TIMEZONE = OFFSET_OPTIONS.find(o => o.value === 'GMT') || OFFSET_OPTIONS[0];
  allEventsChecked = false;
  allEventsIndeterminate = false;
  selectedScheduleEvents: ScheduleEvent[] = []; // user check on check box to select schedules
  private originalEventsSnapshot: Map<string, string> = new Map(); // deep snapshot of events per scheduleId for dirty tracking
  private sessionInitialVersionIds: Map<string, string> = new Map(); // version detail ID loaded at the start of this session, per scheduleId

  // Life cycle methods
  constructor(
    private modalService: NgbModal,
    private calendar: NgbCalendar,
    private programService: ProgramsService,
    private scheduleService: SchedulesService,
  ) {
    this.channelSubject.pipe(distinctUntilChanged())
      .subscribe(channel => {
        const request: ScheduleVersionRequest = {
          channel: channel,
          date: this.requestSubject.getValue()?.date ?? this.TODAY,
        }

        if (this.readOnlyMode) {
          request.timezone = this.GMT_TIMEZONE;
        } else if (channel?.timeZone) {
          request.timezone = OFFSET_OPTIONS.find(o => o.value === channel.timeZone)
        } else {
          request.timezone = this.DEFAULT_TIMEZONE
        }

        this.requestSubject.next(request);
      });
    this.requestSubject.pipe(distinctUntilChanged())
      .subscribe(request => {
        if (this.readOnlyMode) {
          this.scheduleLoadingSubject.next(true);
          this.selectedScheduleVersionsSubject.next([]);
        }
        this.onScheduleVersionRequestChanged.emit(request);
      });
  }

  ngOnInit() {
    this.getRatings();
    this.getQualifiers();
    this.getGenres();
  }

  getGenres() {
    this.programService.getAllGenres().subscribe(data => {
      const langFilters = getLangFilters();
      const genreList = []
      data.response?.forEach((genre) => {
        const filter = genre.names;
        sortByOrders(filter, langFilters, 'language');

        if (filter) {
          genreList.push({
            id: genre.id,
            name: filter[0].value,
          })
        }
      })
      this.genres = genreList
    })
  }

  handleSchedulesChanged(schedules: ScheduleDetails[]) {
    this.scheduleLoadingSubject.next(false);
    this.schedulesSubject.next(schedules);
    schedules.forEach(schedule => {
      // Capture event snapshot the first time each schedule is loaded (after version change, snapshot was cleared so this always fires)
      if (!this.originalEventsSnapshot.has(schedule.scheduleId)) {
        this.originalEventsSnapshot.set(schedule.scheduleId, this.serializeEvents(schedule.events));
      }
      // Capture the initial version detail ID only once per session (NOT reset on version switch, only on new session)
      if (!this.sessionInitialVersionIds.has(schedule.scheduleId)) {
        this.sessionInitialVersionIds.set(schedule.scheduleId, schedule.id);
      }
    });
  }

  // Show the qualifiers modal.
  handleMangingQualifiers(qualifierModal: TemplateRef<any>) {
    this.modalService.open(qualifierModal, {
      size: 'md',
      ariaLabelledBy: 'modal-basic-title',
      keyboard: false,
    });
  }

  // Remove selected events from UI
  handleRemovingEvents() {
    const modal = this.modalService.open(ConfirmationModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    });
    modal.componentInstance.title = 'Remove Schedules';
    modal.componentInstance.message = 'Are you sure you want to remove selected schedules?';
    modal.componentInstance.isAlert = false;
    modal.result.then((result) => {
      if (result) {
        this.schedulesSubject.getValue()?.forEach(schedule => {
          schedule.events = schedule.events.filter(event => !this.selectedScheduleEvents.includes(event));
        });
        this.selectedScheduleEvents = [];
      }
    });
  }

  // Show the blackout modal.
  manageBlackout(blackoutModal: TemplateRef<any>) {
    this.modalService.open(blackoutModal, {
      size: 'md',
      ariaLabelledBy: 'modal-basic-title',
      keyboard: false,
    });
  }

  // Close all opening modals on UI.
  closeModal = () => {
    this.allEventsChecked = false;
    this.selectedScheduleEvents = [];
    this.modalService.dismissAll();
  }

  // Removing blackout regions for selected qualifiers on UI to selected schedules.
  applyBlackout = (mobileBlackout: boolean, selectedRegions: string[]) => {
    let blackout;
    let labels;
    if (mobileBlackout) {
      blackout = true;
      labels = [{value: 'mobile_blackout_CL'}];
    } else {
      blackout = !!selectedRegions?.length;
      labels = [];
    }
    this.selectedScheduleEvents.forEach(e => {
      e.blackout = blackout;
      e.blackoutRegions = selectedRegions;
      e.labels = labels;
      e.modified = true;
    });
    this.closeModal();
  }

  // Apply changes for selected qualifiers on UI to selected schedules.
  applyQualifiers = ($event: any[]) => {
    this.selectedScheduleEvents?.forEach(event => {
      event.qualifiers = $event.map(q => 'epgQualifier' + q);
      event.modified = true;
    });
    this.closeModal();
  }

  // Check if there is any event displayed on UI or not
  hasEvent() {
    return !!this.schedulesSubject.getValue()
      ?.find(s => s.hasEvent(this.requestSubject.getValue().timezone, this.requestSubject.getValue().date));
  }

  // Handle stuff when user check all events
  handleCheckingAllEvents($event: boolean) {
    this.allEventsChecked = $event;
    this.allEventsIndeterminate = false;
    if ($event) {
      let displayedEvents: ScheduleEvent[] = [];
      this.schedulesSubject.getValue()?.forEach(s => {
        displayedEvents = displayedEvents
          .concat(s.displayedEvent(this.requestSubject.getValue()?.timezone, this.requestSubject.getValue()?.date));
      });
      this.selectedScheduleEvents = displayedEvents;
    } else {
      this.selectedScheduleEvents = [];
    }
  }

  // Show or hide the program panel on UI
  handleDisplayingProgramPanel($event: boolean) {
    this.onShowOrHideProgramPanel.emit($event);
  }

  reportProblem() {
    const request = this.requestSubject.getValue();
    const modal = this.modalService.open(ReportProblemModalComponent, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    modal.componentInstance.channelId = request.channel?.id;
    modal.componentInstance.selectedDate = request.date;
    modal.componentInstance.timezone = request.timezone?.value || 'GMT';
    modal.componentInstance.schedules = this.schedulesSubject.getValue();
  }

  // Handle in case there is any event checked on UI
  handleSelectedEventsChanged = (selected: ScheduleEvent[]) => {
    this.selectedScheduleEvents = selected;
    let displayed: ScheduleEvent[] = [];
    this.schedulesSubject.getValue()?.forEach(s => {
      displayed = displayed
        .concat(s.displayedEvent(this.requestSubject.getValue()?.timezone, this.requestSubject.getValue()?.date))
    });
    // Check or uncheck the 'Select All' check box
    this.allEventsChecked = selected && selected.length > 0 ? displayed?.every(e => selected.includes(e)) : false;
    this.allEventsIndeterminate = selected && selected.length > 0 ? !!displayed?.find(e => selected.includes(e)) : false;
  }

  // Get all valid qualifiers from api
  publishSchedules = (): void => {
    if (!this.isValidSchedule()) {
      this.showInvalidScheduleMessages();
      return;
    }
    if (!this.channelSubject?.getValue()?.published) {
      this.warningUnpublishedChannel();
      return;
    }
    const modal = this.modalService.open(ConfirmationModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    });
    const firstVersion = this.selectedScheduleVersionsSubject.getValue().find(v => v.id === this.schedulesSubject.getValue()[0].id);
    const secondVersion = this.selectedScheduleVersionsSubject.getValue().find(v => v.id === this.schedulesSubject.getValue()[1]?.id);
    const submitMessage = `version ${firstVersion?.version ?? 1} of First schedule and version ${secondVersion?.version ?? 1} of Second schedule`;
    modal.componentInstance.title = 'Submit Changes';
    modal.componentInstance.message = `You are about to submit ${this.selectedScheduleVersionsSubject.getValue().length} versions: ${submitMessage}. Continue?`;
    modal.componentInstance.isAlert = false;
    modal.result.then((result) => {
      if (result) {
        this.schedulesSubject.getValue()?.forEach(s => s.published = true);
        this.saveOrPublish(true);
      }
    });
  }

  saveSchedules = (): void => {
    const modal = this.modalService.open(ConfirmationModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    })
    modal.componentInstance.title = 'Submit Changes';
    modal.componentInstance.message = 'Are you sure you want to continue?';
    modal.componentInstance.isAlert = false;
    modal.result.then((result) => {
      if (result) {
        this.saveOrPublish(false);
      }
    })
  };

  updateScheduleDate = (selectedDate: string): void => {
    const request: ScheduleVersionRequest = {
      ...this.requestSubject.getValue(),
      date: parseDate(selectedDate),
    }
    this.requestSubject.next(request);
  };

  updateTimezone = (selectedOffset: Offset): void => {
    const request: ScheduleVersionRequest = {
      ...this.requestSubject.getValue(),
      timezone: selectedOffset ?? this.requestSubject.getValue()?.timezone,
    }
    this.requestSubject.next(request);
  };

  // Get all valid ratings from api
  private getRatings() {
    const ratingFilter = getValidQualifiers();
    this.programService.getAllRatings().subscribe((data: any) => {
      this.ratings = data.response?.filter(rating => ratingFilter?.includes(rating.code));
    })
  }

  private serializeEvents(events: ScheduleEvent[]): string {
    const sort = (arr: any[]): any[] => arr?.length ? [...arr].sort() : [];
    return JSON.stringify((events ?? []).map(e => ({
      id: e.id,
      startDate: e.startDate,
      endDate: e.endDate,
      duration: e.duration,
      programId: e.programId,
      qualifiers: sort(e.qualifiers),
      blackout: e.blackout,
      blackoutRegions: sort(e.blackoutRegions),
      labels: sort(e.labels?.map((l: any) => l.value ?? l)),
    })));
  }

  private isScheduleDirty(schedule: ScheduleDetails): boolean {
    const snapshot = this.originalEventsSnapshot.get(schedule.scheduleId);
    if (snapshot === undefined || snapshot !== this.serializeEvents(schedule.events)) {
      return true;
    }
    // Also dirty if the user switched to a different version than what was loaded at session start
    const initialVersionId = this.sessionInitialVersionIds.get(schedule.scheduleId);
    if (initialVersionId && schedule.id && initialVersionId !== schedule.id) {
      return true;
    }
    return false;
  }

  private saveOrPublish(published: boolean): void {
    this.scheduleLoadingSubject.next(true);
    // Only send schedules that have been changed.
    const dirtySchedules = this.schedulesSubject.getValue().filter(schedule => this.isScheduleDirty(schedule));
    // VLS-4324: Should not create schedule with empty events when publishing schedule
    let schedules = published
      ? dirtySchedules.filter(schedule => schedule.events && schedule.events.length > 0)
      : dirtySchedules;

    if (schedules?.length > 0) {
      schedules.forEach(schedule => schedule.provider = 'VLS'); // reset provider to VLS before saving and publishing.
      this.scheduleService.saveAndPublish(schedules, published)
        .subscribe(
          () => {
            this.scheduleLoadingSubject.next(false);
            // Reset session state so the next load starts fresh with updated version IDs
            this.sessionInitialVersionIds.clear();
            this.showAlert('Success', 'Schedules successfully saved');
            this.onScheduleVersionRequestChanged.emit(this.requestSubject.getValue());
          },
          (err: any) => {
            this.scheduleLoadingSubject.next(false);
            let errMessage = err.error?.errors ? err.error?.errors[0] : 'Submission Failed';
            //format the message when having negative event
            if (errMessage.includes(AppConstants.MESSAGE_NEGATIVE_DURATION)) {
              let timezone = this.requestSubject.getValue()?.timezone.value
              let time = errMessage.split(`${AppConstants.MESSAGE_NEGATIVE_DURATION} `)[1];
              let eventStartTime = dayjs.utc(time?.split(", schedule date ")[0]).tz(timezone).format('ddd, MMM DD - HH:mm:ss')
              let scheduleDate = new Date(time?.split(", schedule date ")[1]).toISOString().split('T')[0]
              errMessage = `${AppConstants.MESSAGE_NEGATIVE_DURATION} ${eventStartTime}, schedule date ${scheduleDate} when saving schedule.`
            }
            this.showAlert(`Error`, `${errMessage}`, `md`);
          });
    } else {
      this.scheduleLoadingSubject.next(false);
      if (dirtySchedules.length === 0) {
        this.showAlert(`Warning`, `No changes detected. Please modify at least one schedule before saving.`);
      } else {
        this.showAlert(`Warning`, `The published schedules don't have any events. Please add at least one event and try again!`);
      }
    }
  }

  private showAlert(type: string, message: string, size: any = 'sm') {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      size,
      centered: true,
      backdrop: 'static',
    })
    modalRef.componentInstance.title = type
    modalRef.componentInstance.message = message
    modalRef.componentInstance.isAlert = true
  }

  private getQualifiers() {
    this.qualifiers = [];
    this.scheduleService.getQualifiers().subscribe(data => {
      this.qualifiers = data.response.map(d => {
        return {
          ...d,
          code: d.name.split('epgQualifier')[1]
        }
      })
    })
  }

  private warningUnpublishedChannel() {
    const modal = this.modalService.open(ConfirmationModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    });
    modal.componentInstance.title = 'Warning';
    modal.componentInstance.message = `The channel ${this.channelSubject?.getValue()?.id} is not published. Please publish this channel first and try again!`;
    modal.componentInstance.isAlert = true;
    modal.result.then((result) => {
      if (result) {
        this.closeModal();
      }
    })
  }

  private showInvalidScheduleMessages() {
    const modal = this.modalService.open(SchedulesErrorModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    })

    let gapEvents: ScheduleEvent[] = [];
    let negativeEvents = [];
    let overlappedEvents = [];
    let errorMessages = [];

    this.schedulesSubject.getValue()?.forEach((s, j) => {
      s.events?.forEach((e, i) => {
        let nextEvent = s.events[i + 1]
        if (i == s.events.length - 1 && j == 0 && this.schedulesSubject.getValue().length == 2) {
          nextEvent = this.schedulesSubject.getValue()[1].events[0]
        }
        if (!e.isPositiveDuration()) {
          negativeEvents.push(e)
        } else if (e.isOverlapped(nextEvent)) {
          overlappedEvents.push(e)
        } else if (e.isGap(nextEvent)) {
          gapEvents.push(e)
        }
      })
    });

    if (negativeEvents.length > 0) {
      let items = `${negativeEvents.map(e => e.formattedStartDate(this.requestSubject.getValue()?.timezone.value)).join(' and ')}`;
      errorMessages.push(`${AppConstants.MESSAGE_NEGATIVE_DURATION} ${items} in local timezone`);
    }

    if (gapEvents.length > 0) {
      let items = `${gapEvents.map(e => e.formattedStartDate(this.requestSubject.getValue()?.timezone.value)).join(' and ')}`;
      errorMessages.push(`Gap event(s) detected at: ${items} in local timezone`);
    }

    if (overlappedEvents.length > 0) {
      let items = `${overlappedEvents.map(e => e.formattedStartDate(this.requestSubject.getValue()?.timezone.value)).join(' and ')}`;
      errorMessages.push(`Overlapped event(s) detected at: ${items} in local timezone`);
    }

    let emptyProgramMessage = null;
    this.schedulesSubject.getValue()?.forEach(s => {
      if (s.events?.length > 0 && s.events.find(e => e.programId == null)) {
        emptyProgramMessage = "Cannot publish an incomplete schedule, please wait for a moment!"
       }
    })
    emptyProgramMessage && errorMessages.push(emptyProgramMessage)

    modal.componentInstance.headerTitle = 'Invalid schedule';
    modal.componentInstance.errorMessages = errorMessages;
    modal.result.then(() => this.closeModal());
  }

  private isValidSchedule(): boolean {
    // Check if having gap/overlap with the 2nd schedule
    if (this.schedulesSubject?.getValue()?.length == 2) {
      let lastIndex = this.schedulesSubject.getValue()[0].events?.length - 1
      return (
        !this.schedulesSubject
          .getValue()[0]
          .events[lastIndex].isGap(
            this.schedulesSubject.getValue()[1].events[0]
          ) &&
        !this.schedulesSubject
          .getValue()[0]
          .events[lastIndex].isOverlapped(
            this.schedulesSubject.getValue()[1].events[0]
          ) &&
        this.schedulesSubject.getValue()?.every((s) => s.isValid())
      )
    }
    return this.schedulesSubject.getValue()?.every(s => s.isValid());
  }
}
